import React, { useState, useEffect } from 'react';
import { Box, TextField, InputAdornment, MenuItem, Select, FormControl, InputLabel, IconButton } from '@mui/material';
import { ArrowUpward, ArrowDownward } from '@mui/icons-material';
import axios from 'axios';
import { API_URL } from '../../utils/auth';
import Auth from '../../utils/auth';
import './Search.css';

const SearchComponent = ({ onStatusChange, onSearch, onSortChange }) => {
    const [status, setStatus] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [sortField, setSortField] = useState('');
    const [sortDirection, setSortDirection] = useState('asc');
    const [availableCSMs, setAvailableCSMs] = useState([]);
    const [selectedCSM, setSelectedCSM] = useState('');
    const [isLoadingCSMs, setIsLoadingCSMs] = useState(false);

    const workspaceId = Auth.getWorkspaceId();
    const userId = Auth.getUser()?.id;
    const user = Auth.getUser();
    const isAdmin = user?.admin || false;

    // Fetch available CSMs when component mounts
    useEffect(() => {
        const fetchAvailableCSMs = async () => {
            if (!workspaceId || !userId || !isAdmin) {
                return;
            }
            setIsLoadingCSMs(true);
            try {
                const response = await axios.get(
                    `${API_URL}/clients/available-csm?workspaceId=${workspaceId}&adminUserId=${userId}`
                );
                setAvailableCSMs(response.data);
            } catch (err) {
                console.error("Failed to fetch available CSMs:", err);
                setAvailableCSMs([]);
            } finally {
                setIsLoadingCSMs(false);
            }
        };

        fetchAvailableCSMs();
    }, [workspaceId, userId, isAdmin]);

    const handleStatusChange = (event) => {
        const newStatus = event.target.value;
        setStatus(newStatus);
        onStatusChange(newStatus); 
    };

    const handleSearch = () => {
        onSearch(searchQuery);
        // Clear selected CSM when doing manual search
        if (selectedCSM && searchQuery !== '') {
            setSelectedCSM('');
        }
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
        // Clear selected CSM when user types manually
        if (selectedCSM && event.target.value !== '') {
            setSelectedCSM('');
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleSearch();
        }
    };

    const handleSortChange = (field) => {
        if (field === '') {
            // Clear sorting
            setSortField('');
            setSortDirection('asc');
            setSelectedCSM('');
            // Clear search when going back to "All"
            setSearchQuery('');
            onSearch('');
            onSortChange('', 'asc');
            return;
        }

        let newDirection = 'asc';

        if (sortField === field) {
            newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            // Reset CSM selection when changing sort field
            setSelectedCSM('');
            // Clear search when changing from CSM to another field
            if (sortField === 'csm') {
                setSearchQuery('');
                onSearch('');
            }
        }

        setSortField(field);
        setSortDirection(newDirection);
        onSortChange(field, newDirection);
    };

    const handleSortIconClick = (e, field) => {
        e.stopPropagation();
        handleSortChange(field);
    };

    const handleCSMChange = (event) => {
        const selectedCSMId = event.target.value;
        setSelectedCSM(selectedCSMId);
        
        if (selectedCSMId) {
            if (selectedCSMId === 'unassigned') {
                // Search for unassigned clients
                onSearch('unassigned');
            } else {
                // Find the selected CSM and search by their name or email
                const csm = availableCSMs.find(c => c.id === selectedCSMId);
                if (csm) {
                    const fullName = csm.firstName && csm.lastName
                        ? `${csm.firstName} ${csm.lastName}`.trim()
                        : csm.email;
                    onSearch(fullName);
                }
            }
        } else {
            // Clear search when no CSM is selected
            onSearch('');
        }
    };

    const sortOptions = [
        { value: 'name', label: 'Name' },
        { value: 'company', label: 'Company' },
        { value: 'lastContacted', label: 'Last Contacted' },
        { value: 'onboardingProgress', label: 'Onboarding Progress' },
        { value: 'clientRetentionScore', label: 'Client Retention Score' },
        { value: 'programs', label: 'Programs' },
        // Only show "Assigned CSM" option for admin users
        ...(isAdmin ? [{ value: 'csm', label: 'Assigned CSM' }] : [])
    ];

    return (
        <Box display="flex" alignItems="center" gap={4}>
            <TextField
                className="search-input"
                variant="outlined"
                placeholder="Search"
                value={searchQuery} 
                onChange={handleSearchChange} 
                onKeyDown={handleKeyDown} 
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <img
                                src="/Search.png"
                                className="search-icon"
                                alt="Search"
                                onClick={handleSearch} 
                                style={{ cursor: 'pointer' }} 
                            />
                        </InputAdornment>
                    ),
                }}
                sx={{
                    width: '30%',
                    backgroundColor: '#F6F4FF',
                    borderRadius: '20px',
                    '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                            border: 'none',
                        },
                    },
                }}
            />
            <FormControl
                className="status-dropdown"
                sx={{
                    minWidth: '20%',
                    backgroundColor: '#F6F4FF',
                    borderRadius: '20px',
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '20px',
                        '& fieldset': {
                            border: 'none',
                        },
                    },
                }}
            >
                <InputLabel>Status</InputLabel>
                <Select
                    value={status}
                    onChange={handleStatusChange}
                    label="Status"
                    sx={{
                        '& .MuiSelect-icon': {
                            color: '#000',
                        },
                    }}
                >
                    <MenuItem value="">All Active</MenuItem>
                    <MenuItem value="Good">Good</MenuItem>
                    <MenuItem value="Fair">Fair</MenuItem>
                    <MenuItem value="At-Risk">At-Risk</MenuItem>
                    <MenuItem value="Archived">Archived Clients</MenuItem>
                </Select>
            </FormControl>

            <Box display="flex" alignItems="center" gap={1}>
                <FormControl
                    className="sort-dropdown"
                    sx={{
                        minWidth: '200px', 
                        backgroundColor: '#F6F4FF',
                        borderRadius: '20px',
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '20px',
                            '& fieldset': {
                                border: 'none',
                            },
                        },
                    }}
                >
                    <InputLabel>Sort</InputLabel>
                    <Select
                        value={sortField}
                        onChange={(e) => handleSortChange(e.target.value)}
                        label="Sort"
                        sx={{
                            '& .MuiSelect-icon': {
                                color: '#000',
                            },
                        }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {sortOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {sortField && sortField !== 'csm' && (
                    <IconButton
                        size="small"
                        onClick={(e) => handleSortIconClick(e, sortField)}
                        sx={{
                            backgroundColor: '#F6F4FF',
                            borderRadius: '16px', 
                            width: '60px', 
                            height: '60px', 
                            padding: 0,
                            '&:hover': {
                                backgroundColor: '#EAEAEA',
                            },
                        }}
                    >
                        {sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}
                    </IconButton>
                )}

                {/* Show CSM selector when "Assigned CSM" is selected */}
                {sortField === 'csm' && isAdmin && (
                    <FormControl
                        className="csm-dropdown"
                        sx={{
                            minWidth: '200px',
                            backgroundColor: '#F6F4FF',
                            borderRadius: '20px',
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '20px',
                                '& fieldset': {
                                    border: 'none',
                                },
                            },
                        }}
                    >
                        <InputLabel>Select CSM</InputLabel>
                        <Select
                            value={selectedCSM}
                            onChange={handleCSMChange}
                            label="Select CSM"
                            disabled={isLoadingCSMs}
                            sx={{
                                '& .MuiSelect-icon': {
                                    color: '#000',
                                },
                            }}
                        >
                            <MenuItem value="">All CSMs</MenuItem>
                            <MenuItem value="unassigned">
                                <span style={{ 
                                    flex: 1, 
                                    overflow: 'hidden', 
                                    textOverflow: 'ellipsis', 
                                    whiteSpace: 'nowrap'
                                }}>
                                    Unassigned
                                </span>
                            </MenuItem>
                            {availableCSMs.map((csm) => (
                                <MenuItem key={csm.id} value={csm.id}>
                                    <span style={{ 
                                        flex: 1, 
                                        overflow: 'hidden', 
                                        textOverflow: 'ellipsis', 
                                        whiteSpace: 'nowrap' 
                                    }}>
                                        {csm.firstName && csm.lastName
                                            ? `${csm.firstName} ${csm.lastName}`.trim()
                                            : csm.email}
                                        <span style={{ 
                                            marginLeft: '8px', 
                                            fontSize: '0.875rem', 
                                            color: '#666',
                                            opacity: 0.7 
                                        }}>
                                            ({csm.clientCount || 0} clients)
                                        </span>
                                    </span>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
            </Box>
        </Box>
    );
};

export default SearchComponent;